/**
 * Yemeksepeti teslimat tipi çözümleyicisi.
 *
 * Yemeksepeti (Delivery Hero) siparişleri üç şekilde gelir:
 *   - PICKUP          → müşteri siparişi restorandan kendisi teslim alır ("Gel Al")
 *   - OWN_DELIVERY    → Yemeksepeti'nin kendi kuryesi teslim eder
 *   - VENDOR_DELIVERY → restoran kendi kuryesiyle teslim eder
 *
 * Backend `GetOrderType` bu üç değeri de üretir (`YemeksepetiOrderService.cs`),
 * fakat frontend uzun süre yalnız iki dallı bir kontrol yaptığı için PICKUP
 * siparişler "Restoran Kuryesi" olarak görünüyordu. Tek kaynak burası olsun
 * diye modal, fiş, mutfak fişi ve liste dönüşümleri bu helper'ı kullanır.
 */

export type YsDeliveryKind = 'pickup' | 'platformCourier' | 'vendorCourier';

/** Backend camelCase ya da PascalCase dönebildiği için ikisi de okunur. */
export interface YsDeliveryTypeSource {
  orderType?: string | null;
  OrderType?: string | null;
  expeditionType?: string | null;
  ExpeditionType?: string | null;
}

export const getYsDeliveryKind = (order?: YsDeliveryTypeSource | null): YsDeliveryKind => {
  const orderType = (order?.orderType ?? order?.OrderType ?? '').toString().toUpperCase();
  const expedition = (order?.expeditionType ?? order?.ExpeditionType ?? '').toString().toUpperCase();

  if (orderType === 'PICKUP' || expedition === 'PICKUP') return 'pickup';
  if (orderType === 'OWN_DELIVERY' || expedition === 'DELIVERY_BY_COURIER') return 'platformCourier';
  return 'vendorCourier';
};

export const isYsPickupOrder = (order?: YsDeliveryTypeSource | null): boolean =>
  getYsDeliveryKind(order) === 'pickup';

const UI_LABELS: Record<YsDeliveryKind, string> = {
  pickup: 'Gel Al (Müşteri Alacak)',
  platformCourier: 'Yemeksepeti Kurye',
  vendorCourier: 'Restoran Kuryesi',
};

// Fiş etiketleri termal yazıcıya ASCII gider ve 32 sütuna sığmalıdır.
const RECEIPT_LABELS: Record<YsDeliveryKind, string> = {
  pickup: '*** GEL AL - MUSTERI ALACAK ***',
  platformCourier: 'Yemeksepeti Kurye',
  vendorCourier: 'Restoran Kuryesi',
};

export const getYsDeliveryLabel = (
  order?: YsDeliveryTypeSource | null,
  variant: 'ui' | 'receipt' = 'ui',
): string => (variant === 'receipt' ? RECEIPT_LABELS : UI_LABELS)[getYsDeliveryKind(order)];

/**
 * `pickup.pickupTime` (UTC) → Istanbul saatinde "HH:mm".
 * Müşterinin siparişi almaya geleceği saattir; mutfak sıralaması için kritik.
 */
export const formatYsPickupTime = (pickupTime?: string | null): string | undefined => {
  if (!pickupTime) return undefined;
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(pickupTime);
  const date = new Date(hasTimezone ? pickupTime : `${pickupTime}Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat('tr-TR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Istanbul',
  }).format(date);
};
